import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../services/store';
import { PURGE } from 'redux-persist';
import { UserType } from '../types/common';
import { MembershipInfo } from '../types/main';

interface AuthState {
  accessToken?: string;
  refreshToken?: string;
  user?: {
    alias: string;
    email: string;
    userId: number;
    type: UserType;
    membershipInfo: MembershipInfo;
  } | null;
  loginType?: string;
}

const initialState: AuthState = {
  accessToken: '',
  refreshToken: '',
  user: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials: (
      state,
      {
        payload: { accessToken, refreshToken, user, loginType },
      }: PayloadAction<AuthState>
    ) => {
      accessToken !== undefined && (state.accessToken = accessToken);
      refreshToken !== undefined && (state.refreshToken = refreshToken);
      user !== undefined && (state.user = user);
      loginType && (state.loginType = loginType);
    },
    clearCredentials: () => initialState,
    aliasChanged: (state, { payload }) => {
      if (state.user) {
        state.user.alias = payload;
      }
    },
    membershipInfoChanged: (state, { payload }) => {
      if (state.user) {
        state.user.membershipInfo = payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(PURGE, () => initialState);
  },
});

export const {
  setCredentials,
  clearCredentials,
  aliasChanged,
  membershipInfoChanged,
} = authSlice.actions;

export default authSlice.reducer;

export const selectTokens = createSelector(
  (state: RootState) => state.auth,
  (auth) => ({
    accessToken: auth?.accessToken,
    refreshToken: auth?.refreshToken,
  })
);

export const selectUser = (state: RootState) => state.auth?.user;
export const selectUserEmail = (state: RootState) => state.auth?.user?.email;
export const selectUserAlias = (state: RootState) => state.auth?.user?.alias;
export const selectUserId = (state: RootState) => state.auth?.user?.userId;
export const selectUserType = (state: RootState) => state.auth?.user?.type;
export const selectLoginType = (state: RootState) => state.auth.loginType;
export const selectMembershipInfo = (state: RootState) =>
  state.auth?.user?.membershipInfo;
